import { useState, useEffect, useCallback } from 'react';
import { C, SCHEDULER_BACKEND } from '../constants';

const STATUS_STYLE = {
  confirmed_live:    { bg: '#EAF3DE', color: '#3B6D11', label: 'Confirmed live' },
  assumed_live:      { bg: '#E6F1FB', color: '#185FA5', label: 'Assumed live' },
  attempted:         { bg: '#FEF9EC', color: '#BA7517', label: 'Attempted' },
  unconfirmed_stale: { bg: '#f1f5f9', color: '#64748b', label: 'Unconfirmed' },
  failed:            { bg: '#FEF2F2', color: '#dc2626', label: 'Failed' },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || { bg: '#f1f5f9', color: '#64748b', label: status };
  return (
    <span style={{ fontSize: 11, background: s.bg, color: s.color, borderRadius: 6, padding: '2px 8px', whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  );
}

// Server-side, persistent record of what actually got published — as
// opposed to the sections above this one, which are derived from this
// browser's localStorage. This is the standard the user set when demanding
// real, independently-checkable public links instead of trusting a
// publish call's own "success" response.
export default function PublishAuditLog() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [cadence, setCadence] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bumping, setBumping] = useState(false);

  const load = useCallback(async () => {
    try {
      const [rowsRes, summaryRes, cadenceRes, alertsRes] = await Promise.all([
        fetch(`${SCHEDULER_BACKEND}/audit?limit=50`).then((r) => r.json()),
        fetch(`${SCHEDULER_BACKEND}/audit/summary`).then((r) => r.json()),
        fetch(`${SCHEDULER_BACKEND}/audit/cadence`).then((r) => r.json()),
        fetch(`${SCHEDULER_BACKEND}/audit/alerts?resolved=false`).then((r) => r.json()),
      ]);
      setRows(rowsRes.rows || []);
      setSummary(summaryRes || null);
      setCadence(cadenceRes.cadence || null);
      setAlerts(alertsRes.alerts || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function bumpToTwicePerWeek() {
    setBumping(true);
    try {
      await fetch(`${SCHEDULER_BACKEND}/audit/cadence`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ideasPerRun: 2 }),
      });
      await load();
    } finally {
      setBumping(false);
    }
  }

  async function dismissAlert(id) {
    await fetch(`${SCHEDULER_BACKEND}/audit/alerts/${id}/resolve`, { method: 'PATCH' });
    setAlerts((prev) => prev.filter((a) => a.id !== id));
  }

  if (loading) return <div style={{ fontSize: 12, color: '#94a3b8', padding: '12px 0' }}>Loading publish log...</div>;
  if (error) return <div style={{ fontSize: 12, color: '#dc2626', padding: '12px 0' }}>Error: {error}</div>;

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Confirmed publish log — server-verified</div>
        {cadence && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, color: C.muted }}>
              {cadence.ideas_per_run} idea{Number(cadence.ideas_per_run) !== 1 ? 's' : ''}/{cadence.run_interval_days === 7 ? 'week' : `${cadence.run_interval_days}d`}
              {cadence.next_due_at && ` · next batch ${new Date(Number(cadence.next_due_at)).toLocaleDateString()}`}
            </span>
            {Number(cadence.ideas_per_run) < 2 && (
              <button
                onClick={bumpToTwicePerWeek}
                disabled={bumping}
                style={{ padding: '4px 10px', fontSize: 11, border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', cursor: 'pointer', color: C.text }}
              >
                {bumping ? 'Updating…' : 'Bump to 2/week'}
              </button>
            )}
          </div>
        )}
      </div>

      {alerts.map((a) => (
        <div key={a.id} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, background: '#FEF2F2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
          <div style={{ fontSize: 12, color: '#991b1b' }}><strong>{a.source}:</strong> {a.message}</div>
          <button onClick={() => dismissAlert(a.id)} style={{ padding: '3px 8px', fontSize: 11, border: '1px solid #fca5a5', borderRadius: 6, background: '#fff', cursor: 'pointer', color: '#dc2626', flexShrink: 0 }}>Dismiss</button>
        </div>
      ))}

      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 14 }}>
          {[
            { n: summary.confirmedLive, l: 'Confirmed live', bg: '#EAF3DE', color: '#3B6D11' },
            { n: summary.totalAttempted - summary.confirmedLive - summary.failed, l: 'Awaiting confirmation', bg: '#FEF9EC', color: '#BA7517' },
            { n: summary.failed, l: 'Failed', bg: '#FEF2F2', color: '#dc2626' },
          ].map((s, i) => (
            <div key={i} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 14px' }}>
              <div style={{ fontSize: 20, fontWeight: 500, color: C.text, lineHeight: 1 }}>{s.n}</div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>{s.l}</div>
            </div>
          ))}
        </div>
      )}

      {rows.length === 0 ? (
        <div style={{ fontSize: 12, color: C.muted, padding: '8px 0' }}>Nothing published yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rows.map((row) => (
            <div key={row.id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 12px' }}>
              <StatusBadge status={row.status} />
              <span style={{ fontSize: 11, background: '#f1f5f9', color: '#475569', borderRadius: 4, padding: '2px 6px', flexShrink: 0 }}>{row.platform}</span>
              <span style={{ fontSize: 12, color: C.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {row.title || row.content_snippet || '(no content)'}
              </span>
              <span style={{ fontSize: 11, color: C.muted, flexShrink: 0 }}>
                {new Date(Number(row.attempted_at)).toLocaleDateString()}
              </span>
              {row.real_link ? (
                <a href={row.real_link} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#185FA5', flexShrink: 0 }}>View →</a>
              ) : (
                <span style={{ fontSize: 11, color: '#cbd5e1', flexShrink: 0 }}>—</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
